import { Wrapper } from "@googlemaps/react-wrapper";
import { Box, Paper, Stack } from "@mui/material";
import { IconUsers } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { formatTeamSize, getMaxPlayersFromTeamSize } from "../../utils/game";
import Button from "../ui/Button";
import Text from "../ui/Text";

// Game location marker component
const GameMarker = ({ map, position, game, onClick, isSelected }) => {
  const markerRef = useRef(null);

  useEffect(() => {
    if (!map || !window.google) return;

    // Create SVG pin icon with rounded shape
    const color = isSelected ? "#1890FF" : "#000000";
    const size = isSelected ? 38 : 32;

    const svgIcon = {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
        <svg width="${size}" height="${size * 1.15}" viewBox="0 0 32 36" xmlns="http://www.w3.org/2000/svg">
          <path fill="${color}" d="M16 2C10.48 2 6 6.48 6 12c0 3.5 2 6.5 4.5 9.5C13 24 16 28 16 34c0-6 3-10 5.5-12.5C24 18.5 26 15.5 26 12c0-5.52-4.48-10-10-10z"/>
          <circle fill="white" cx="16" cy="12" r="4.5"/>
        </svg>
      `)}`,
      scaledSize: new window.google.maps.Size(size, size * 1.15),
      anchor: new window.google.maps.Point(size / 2, size * 1.15),
    };

    // const svgIcon2 = {
    //   url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
    //     <svg width="${size}" height="${size * 1.15}" viewBox="0 0 14 20" xmlns="http://www.w3.org/2000/svg">
    //       <path fill="${color}" d="M7 0C3.134 0 0 3.134 0 7c0 3.866 7 13 7 13s7-9.134 7-13c0-3.866-3.134-7-7-7z"/>
    //       <circle fill="white" cx="7" cy="7" r="3"/>
    //     </svg>
    //   `)}`,
    //   scaledSize: new window.google.maps.Size(size, size * 1.15),
    //   anchor: new window.google.maps.Point(size / 2, size * 1.15),
    // };

    // const svgIcon3 = {
    //   url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
    //     <svg width="${size}" height="${size}" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    //       <path fill="${color}" fill-rule="evenodd" clip-rule="evenodd" d="M12 20C12 20 19 16.866 19 11C19 7.13401 15.866 4 12 4C8.13401 4 5 7.13401 5 11C5 16.866 12 20 12 20Z"/>
    //       <circle fill="white" cx="12" cy="11" r="3"/>
    //     </svg>
    //   `)}`,
    //   scaledSize: new window.google.maps.Size(size, size),
    //   anchor: new window.google.maps.Point(size / 2, size),
    // };

    // Create custom marker
    const marker = new window.google.maps.Marker({
      position,
      map,
      icon: svgIcon,
      title: game.name || game.venue,
    });

    // Add click listener
    marker.addListener("click", () => {
      onClick(game);
    });

    markerRef.current = marker;

    return () => {
      if (markerRef.current) {
        markerRef.current.setMap(null);
      }
    };
  }, [map, position, game, onClick, isSelected]);

  return null;
};

// Main map component
const MapComponent = ({ games, selectedGame, onGameSelect }) => {
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);

  useEffect(() => {
    if (!mapRef.current || !window.google) return;

    // Initialize map centered on Dublin
    const mapInstance = new window.google.maps.Map(mapRef.current, {
      center: { lat: 53.3498, lng: -6.2603 }, // Dublin coordinates
      zoom: 12,
      styles: [
        // Custom map styling to match your image
        {
          featureType: "poi",
          elementType: "labels",
          stylers: [{ visibility: "off" }],
        },
      ],
      disableDefaultUI: false,
      zoomControl: true,
      mapTypeControl: false,
      scaleControl: false,
      streetViewControl: false,
      rotateControl: false,
      fullscreenControl: false,
    });

    setMap(mapInstance);
  }, []);

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
      <div ref={mapRef} style={{ width: "100%", height: "100%" }} />

      {/* Render game markers */}
      {games.map((game) => {
        const position = { lat: game.location.lat, lng: game.location.lng };

        return (
          <GameMarker
            key={game.id}
            map={map}
            position={position}
            game={game}
            onClick={onGameSelect}
            isSelected={selectedGame?.id === game.id}
          />
        );
      })}
    </Box>
  );
};

// Game info card that appears at bottom
const GameInfoCard = ({ game, onViewDetails }) => {
  if (!game) return null;

  const time = new Date(game.datetime).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
  const description = `${formatTeamSize(game.players)} ${game.sport} with ${game.creator_name}`;
  const currentPlayers = game?.active_players?.length || 0;
  const maxPlayers = getMaxPlayersFromTeamSize(game.players);

  return (
    <Paper
      elevation={3}
      sx={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        p: 1,
        mx: 2,
        mb: 2,
        borderRadius: "6px",
        zIndex: 1000,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Text variant="cardHeader" sx={{ fontWeight: 600 }}>
          {game.venue}
        </Text>
        <Text variant="paragraph3" sx={{ fontWeight: 500 }}>
          {time}
        </Text>
      </Box>

      <Stack spacing={1}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            justifyContent: "space-between",
          }}
        >
          <Text variant="paragraph3" sx={{ color: "#666" }}>
            {description}
          </Text>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconUsers size={16} color="#666" />
            <Text variant="paragraph3" sx={{ color: "#666" }}>
              {currentPlayers}/{maxPlayers}
            </Text>
          </Box>
        </Box>

        <Button
          variant="primary"
          onClick={() => onViewDetails(game)}
          sx={{ mt: 2 }}
        >
          View game details
        </Button>
      </Stack>
    </Paper>
  );
};

// Main GameLocationMap component
const GameLocationMap = ({ games, onGameClick }) => {
  const [selectedGame, setSelectedGame] = useState(null);
  const cardRef = useRef(null);

  const handleGameSelect = (game) => {
    setSelectedGame(game);
  };

  const handleCloseGameInfo = () => {
    setSelectedGame(null);
  };

  const handleViewDetails = (game) => {
    setSelectedGame(null);
    onGameClick(game);
  };

  useEffect(() => {
    if (!selectedGame) return;
    const handleClickOutside = (event) => {
      if (cardRef.current && !cardRef.current.contains(event.target)) {
        setSelectedGame(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectedGame]);

  const render = (status) => {
    if (status === "LOADING") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "400px",
          }}
        >
          <Text>Loading map...</Text>
        </Box>
      );
    }

    if (status === "FAILURE") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "400px",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Text>Failed to load map</Text>
          <Text variant="paragraph3" sx={{ color: "#666" }}>
            Please check your Google Maps API key
          </Text>
        </Box>
      );
    }

    return null;
  };

  const onLoad = () => {};

  return (
    <Box
      sx={{
        position: "relative",
        height: "calc(100vh - 210px)",
        width: "100%",
      }}
    >
      <Wrapper
        apiKey={
          process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
          "AIzaSyC_DfGGmFhYs0EABy_4lE1jPDN0r379XNc"
        }
        render={render}
        libraries={["places"]}
        onLoad={onLoad}
      >
        <MapComponent
          games={games}
          selectedGame={selectedGame}
          onGameSelect={handleGameSelect}
          onGameClick={onGameClick}
        />
      </Wrapper>

      {/* Game info card */}
      <div ref={cardRef}>
        <GameInfoCard
          game={selectedGame}
          onClose={handleCloseGameInfo}
          onViewDetails={handleViewDetails}
        />
      </div>
    </Box>
  );
};

export default GameLocationMap;
